import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileText,
  FolderOpen,
  ListTree,
  Map as MapIcon,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { api, background } from "../../api/client";
import type { DeclinationItem, DeclinationState, Exercise, GroupedNode, Zone } from "../../api/types";
import { Callout } from "../../components/Callout";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { ProgressBar } from "../../components/Gauge";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ViewHeader } from "../../components/ViewHeader";
import { Badge, Button, IconButton, inputClass, SectionLabel } from "../../components/ui";
import { dossierZone } from "../../app/home/dossier";
import { unitDesignation } from "../../i18n/designation";
import { k, lang, t, tServer } from "../../i18n/i18n";
import { levelLabel } from "../../missions";
import { useOpenZone, ZONES_KEY } from "../../map/zones/useZones";
import { orderImageUrl } from "../order/mapImages";
import { showZoneWait } from "../../map/zones/zoneAttention";
import { zoneReady } from "../../map/zones/zoneState";
import { useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { DossierEmptyState } from "../DossierEmptyState";
import { ExportMenu } from "../order/ExportMenu";
import { OrderPage } from "../order/OrderPage";
import { unitMentions, useUnitPeek } from "../order/UnitPeek";
import {
  actionLabel,
  declinable,
  GENERATE_TARGET,
  type GroupedSection,
  groupedSections,
  itemTree,
  nextAction,
  noticeText,
  noticeTone,
  existingText,
  plannedText,
  primaryKind,
  subordinateBadge,
  visibleNotices,
} from "./declination";
import { HigherOrderBanner } from "./HigherOrderBanner";
import { ChiefOrderBanner } from "../received/ChiefOrderNotice";
import { Waiting } from "../../components/Waiting";

/** Ordres subordonnés (docs/20 §13 ; décision d'Alexis du 06/10/2026, « Un seul document regroupé ») : depuis un
 *  dossier de brigade, Generex rédige d'un geste l'ordre de chacun de ses GTIA ; depuis une division, celui de chaque
 *  brigade puis de leurs GTIA. Chaque subordonné reçoit son dossier (sa carte, son fuseau dans celui du chef, son OPO
 *  rédigé) ; la vue montre l'avancement, puis le document regroupé — sommaire, parties communes une seule fois, une
 *  partie par subordonné avec ses paragraphes à revoir et l'accès à sa carte — et l'exporte (PDF, Word, LibreOffice,
 *  texte). L'action attendue (générer, reprendre, mettre à jour) est le bouton principal, qui pulse. */

const POLL_MS = 1500;
const OVERVIEW = "overview";

type Format = "pdf" | "docx" | "odt" | "txt";
const FORMATS: readonly Format[] = ["pdf", "docx", "odt", "txt"];
const HINTS: Partial<Record<Format, string>> = {
  pdf: k("Document regroupé mis en page, cartes comprises"),
  docx: k("Document regroupé modifiable dans Word, cartes comprises"),
  odt: k("LibreOffice, OpenOffice, cartes comprises"),
  txt: k("Texte brut, sans les cartes"),
};

/** Où ouvrir le dossier d'un subordonné : sa carte, son ordre, ou ses Données initiales (mission à choisir). */
type OpenTarget = "map" | "order" | "mission";

/** Ouvre le dossier d'un subordonné sur sa carte (ou l'attente de son terrain libre), son ordre ou ses Données
 *  initiales ; l'avis ramène aux ordres subordonnés du dossier quitté. */
function useOpenDossier(back: { id: string; slug: string | null; name: string } | null) {
  const queryClient = useQueryClient();
  const setExercise = useUi((s) => s.setExercise);
  const setView = useUi((s) => s.setView);
  const showObjects = useUi((s) => s.showObjects);
  const notify = useUi((s) => s.notify);
  return (id: string, target: OpenTarget) => {
    const exercises = queryClient.getQueryData<Exercise[]>(["exercises"]) ?? [];
    const ex = exercises.find((e) => e.id === id);
    const slug = ex?.map_slug ?? null;
    setExercise(id, slug);
    const zone = dossierZone(queryClient.getQueryData<Zone[]>(ZONES_KEY), slug);
    if (target === "order") setView("order");
    else if (target === "mission") setView("mission");
    else if (zone && !zoneReady(zone)) showZoneWait(zone);
    else showObjects("all");
    if (back)
      notify({
        text: t("Dossier ouvert : {name}", { name: ex?.name ?? "" }),
        tone: "info",
        action: {
          label: t("Revenir aux ordres subordonnés"),
          run: () => {
            setExercise(back.id, back.slug);
            setView("declination");
          },
        },
      });
  };
}

export function DeclinationView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const setView = useUi((s) => s.setView);
  const queryClient = useQueryClient();
  const writeBlock = useWriteBlock();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const level = exercise?.parameters.level ?? null;
  const key = ["declination", exerciseId] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => background(() => api.declination(exerciseId as string)),
    enabled: Boolean(exerciseId && exercise && declinable(level)),
    staleTime: 0,
    refetchInterval: (q) => (q.state.data?.declination?.status === "running" ? POLL_MS : false),
  });
  const state = query.data;
  const run = state?.declination ?? null;
  const { zone } = useOpenZone();
  const [starting, setStarting] = useState(false);
  const [resumeFailed, setResumeFailed] = useState(false);
  const tried = useRef<Set<string>>(new Set());
  const previous = useRef<string | null>(null);

  const start = async (silent = false) => {
    if (!exerciseId) return false;
    setStarting(true);
    try {
      queryClient.setQueryData<DeclinationState>(key, await api.startDeclination(exerciseId, lang()));
      setResumeFailed(false);
      return true;
    } catch (e) {
      // Déjà en cours sur un autre poste : l'état est relu, l'avancement s'affiche.
      void queryClient.invalidateQueries({ queryKey: key });
      if (!silent) notify({ text: t("Génération impossible"), detail: (e as Error).message, tone: "error" });
      return false;
    } finally {
      setStarting(false);
    }
  };

  // Exécution interrompue (serveur redémarré) : relancée d'office une fois ; si elle s'interrompt de nouveau ou que
  // la relance échoue, « Reprendre la génération » devient l'action attendue.
  useEffect(() => {
    if (!run || run.status !== "interrupted" || writeBlock.blocked) return;
    const mark = `${run.id}:${run.started_at ?? ""}`;
    if (tried.current.has(mark)) {
      setResumeFailed(true);
      return;
    }
    tried.current.add(mark);
    void start(true).then((ok) => {
      if (!ok) setResumeFailed(true);
    });
  }, [run?.status, run?.id, run?.started_at]);

  // Fin d'une exécution suivie : les dossiers créés arrivent dans la liste, le document regroupé est relu.
  useEffect(() => {
    const status = run?.status ?? null;
    if (previous.current === "running" && status && status !== "running") {
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      void queryClient.invalidateQueries({ queryKey: ["grouped", exerciseId] });
      if (status === "done") {
        const done = run!.items.filter((i) => i.status === "done").length;
        notify({
          text: done <= 1 ? t("{n} ordre rédigé", { n: done }) : t("{n} ordres rédigés", { n: done }),
          detail: t("Chaque subordonné a son dossier, sa carte et son OPO ; le document regroupé est prêt."),
          tone: "ok",
        });
      }
    }
    previous.current = status;
  }, [run?.status]);

  const next = nextAction(state, resumeFailed);
  const kind = primaryKind(state);
  const running = run?.status === "running" || starting;
  const terrainBlocked = () => {
    const reason = state?.reason ?? "";
    notify({ text: t("Génération impossible"), detail: tServer(reason), tone: "info" });
    if (zone && !zoneReady(zone)) showZoneWait(zone);
    else if (!exercise?.map_slug) setView("mission");
  };
  // Téléphone : l'en-tête garde le bouton et « Exporter » sur sa largeur, le libellé court dit l'action.
  const phone = useMediaQuery("(max-width: 639.98px)");
  const blocked = writeBlock.blocked || (state ? !state.available : false);
  const onBlocked = writeBlock.blocked ? writeBlock.onBlocked : terrainBlocked;
  const primary = (
    <Button
      size="sm"
      title={phone && run ? actionLabel(level, kind) : undefined}
      data-attention={GENERATE_TARGET}
      variant={next || (!run && !state?.existing) ? "primary" : "secondary"}
      // Ordre du chef de ce dossier changé lui aussi : l'action attendue est d'abord celle du bandeau (mise à jour
      // depuis le chef, qui reprend l'ordre de ce dossier) ; un seul bouton pulse. Si le bandeau n'a pas de
      // bouton qui pulse (dossier du chef non modifiable ou non partagé), l'action attendue reste celle-ci.
      highlight={
        Boolean(next) && !blocked && !running && !(state?.received?.outdated && state.received.from?.editable)
      }
      disabled={running}
      blocked={blocked}
      onBlocked={onBlocked}
      onClick={() => void start()}
    >
      {running ? <Spinner decorative /> : kind === "generate" ? <Sparkles size={14} /> : <RefreshCw size={14} />}{" "}
      {running ? <Waiting>{t("Génération en cours…")}</Waiting> : phone && run ? shortLabel(kind) : actionLabel(level, kind)}
    </Button>
  );

  const ready = Boolean(exerciseId && exercise);
  const back = exercise ? { id: exercise.id, slug: exercise.map_slug, name: exercise.name } : null;
  const openDossier = useOpenDossier(back);
  const exportReady = Boolean(run?.document) && run?.status === "done";

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="declination"
        context={exercise?.name}
        helpTopic="ordre.decliner"
        badges={run && <RunBadge state={state!} />}
        actions={
          ready &&
          declinable(level) &&
          run && (
            <>
              {/* Exécution interrompue : le bouton de reprise est dans la carte d'avancement, un seul à la fois. */}
              {run.status !== "interrupted" && primary}
              {exportReady && exerciseId && (
                <ExportMenu
                  formats={FORMATS}
                  href={(format) => api.groupedDocumentUrl(exerciseId, format as Format)}
                  hints={HINTS}
                  variant={next ? "secondary" : "primary"}
                  title={t("Exporter le document regroupé")}
                />
              )}
            </>
          )
        }
      />
      {/* Dossier lui-même issu d'une déclinaison : l'ordre de son chef a changé depuis (audit du 10/10/2026). */}
      <HigherOrderBanner exercise={exercise} />
      {/* Dossier transmis : l'ordre de son chef a changé depuis la transmission (ordres transmis prévenus). */}
      <ChiefOrderBanner exercise={exercise} />
      <div className="relative min-h-0 flex-1">
        {!ready ? (
          <DossierEmptyState view="declination" kind="none">
            {t("Depuis un dossier de GTIA, de brigade, de division ou de corps d'armée, Generex rédige l'ordre de chaque subordonné et les réunit dans un seul document.")}
          </DossierEmptyState>
        ) : !declinable(level) ? (
          <NotDeclinable exercise={exercise!} onOpenParent={(id) => openDossier(id, "map")} />
        ) : query.isError ? (
          <EmptyState variant="error">{query.error.message}</EmptyState>
        ) : !state ? (
          <EmptyState variant="loading" title={t("Lecture des ordres subordonnés…")} />
        ) : !run ? (
          <Motion origin="fade" className="h-full">
            <EmptyState
              icon={ListTree}
              title={
                level === "CORPS"
                  ? t("Ordres des divisions et des brigades")
                  : level === "DIVISION"
                    ? t("Ordres des brigades et des GTIA")
                    : level === "REGIMENT"
                      ? t("Ordres des S/GTIA")
                      : t("Ordres des GTIA")
              }
              action={primary}
              note={
                !state.available
                  ? tServer(state.reason ?? "")
                  : state.existing
                    ? existingText(state.existing)
                    : plannedText(state.planned)
              }
            >
              {state.existing
                ? t("Les subordonnés de ce dossier ont déjà leur dossier et leur ordre, reçus de l'échelon supérieur. Mettez-les à jour depuis ce dossier pour qu'ils suivent son ordre et les réunir dans un seul document ; ce que vous y avez modifié vous-même est gardé.")
                : level === "CORPS"
                ? t("Generex rédige l'ordre de chaque division d'après celui du corps (sa mission, l'intention du corps, son fuseau découpé dans la zone rapprochée du corps de part et d'autre de la limite entre divisions), puis l'ordre de chacune de ses brigades d'après celui de sa division. Chacun reçoit son dossier sur le terrain du corps : sa carte, son fuseau, ses LIMA et leurs GDH, sa mission. Les ordres sont réunis dans un seul document.")
                : level === "DIVISION"
                ? t("Generex rédige l'ordre de chaque brigade d'après celui de la division, puis l'ordre de chacun de leurs GTIA d'après celui de leur brigade. Chacun reçoit son dossier : sa carte, son fuseau dans celui de son chef, ses LIMA et leurs GDH, sa mission. Les ordres sont réunis dans un seul document.")
                : level === "REGIMENT"
                ? t("Generex rédige l'ordre de chaque S/GTIA d'après celui du GTIA : sa mission telle que le GTIA la lui donne, la mission de chacune de ses sections ou de ses pelotons. Chacun reçoit son dossier : sa carte, sa zone dans le fuseau du GTIA, les LIMA du GTIA et leurs GDH. Les ordres sont réunis dans un seul document.")
                : t("Generex rédige l'ordre de chaque GTIA d'après celui de la brigade. Chacun reçoit son dossier : sa carte, son fuseau dans celui de la brigade, ses LIMA et leurs GDH, sa mission. Les ordres sont réunis dans un seul document.")}
            </EmptyState>
          </Motion>
        ) : run.status === "running" || run.status === "interrupted" ? (
          <Motion origin="fade" className="h-full overflow-y-auto">
            <Progress state={state} resumeFailed={resumeFailed} primary={primary} onOpen={openDossier} />
          </Motion>
        ) : (
          <Motion origin="fade" className="h-full">
            <GroupedDocumentPane exerciseId={exercise!.id} state={state} onOpen={openDossier} onUpdate={() => void start()} />
          </Motion>
        )}
      </div>
    </div>
  );
}

/** Libellé court du bouton principal (téléphone). */
function shortLabel(kind: "generate" | "update" | "resume"): string {
  return kind === "resume" ? t("Reprendre") : kind === "update" ? t("Mettre à jour") : t("Générer");
}

/** État de la dernière exécution, à côté du titre. */
function RunBadge({ state }: { state: DeclinationState }) {
  const run = state.declination!;
  if (run.status === "running") return <Badge tone="run" dot>{t("En cours")}</Badge>;
  if (run.status === "interrupted") return <Badge tone="warn" dot>{t("Interrompue")}</Badge>;
  if (run.status === "failed") return <Badge tone="danger" dot>{t("Échec")}</Badge>;
  if (run.outdated || run.new_items?.length) return <Badge tone="warn" dot>{t("À mettre à jour")}</Badge>;
  return <Badge>{run.progress.total}</Badge>;
}

/** Dossier d'un autre niveau : la vue explique d'où se génèrent les ordres subordonnés ; un dossier lui-même issu
 *  d'une déclinaison ou d'une transmission mène au dossier de son chef. */
function NotDeclinable({ exercise, onOpenParent }: { exercise: Exercise; onOpenParent: (id: string) => void }) {
  const chain = (exercise.parameters.extra?.chain ?? null) as { from?: string; from_name?: string } | null;
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const parent = chain?.from ? exercises.find((e) => e.id === chain.from) : undefined;
  return (
    <EmptyState
      icon={ListTree}
      title={t("Ordres subordonnés")}
      action={
        parent && (
          <Button variant="secondary" onClick={() => onOpenParent(parent.id)}>
            <FolderOpen size={14} /> {t("Ouvrir le dossier de l'échelon supérieur")}
          </Button>
        )
      }
      note={parent ? t("Ordre reçu de « {name} ».", { name: parent.name }) : undefined}
    >
      {exercise.parameters.level === "COMPAGNIE"
        ? // Niveau compagnie (docs/21) : le dernier niveau, ses sections ou pelotons n'ont pas de dossier.
          t("L'ordre d'un S/GTIA s'adresse à ses sections ou à ses pelotons, qui n'ont pas de dossier propre : il n'a pas d'ordres subordonnés.")
        : t("Les ordres subordonnés se génèrent depuis un dossier de GTIA, de brigade, de division ou de corps d'armée.")}
    </EmptyState>
  );
}

/** Statut d'un subordonné : icône et libellé ; pendant la génération, seul celui en cours de rédaction tourne (le
 *  serveur rédige un subordonné à la fois), les suivants attendent. */
function ItemStatus({ item, running, current }: { item: DeclinationItem; running: boolean; current: boolean }) {
  if (item.status === "done")
    return (
      <span className="inline-flex items-center gap-1 text-meta text-ok">
        <Check size={14} aria-hidden /> {item.action === "updated" ? t("Mis à jour") : t("Créé")}
      </span>
    );
  if (item.status === "failed")
    return (
      <span className="inline-flex items-center gap-1 text-meta text-danger">
        <CircleAlert size={14} aria-hidden /> {t("Non rédigé")}
      </span>
    );
  if (item.status === "skipped") return <span className="text-meta text-ink-muted">{t("Laissé de côté")}</span>;
  if (item.status === "removed")
    return (
      <span className="inline-flex items-center gap-1 text-meta text-warn">
        <CircleAlert size={14} aria-hidden /> {t("Retiré de l'ordre")}
      </span>
    );
  if (running && current)
    return (
      <span className="inline-flex items-center gap-1 text-meta text-ink-soft">
        <Spinner decorative /> {<Waiting>{t("Rédaction…")}</Waiting>}
      </span>
    );
  return <span className="text-meta text-ink-muted">{running ? t("En attente") : t("À rédiger")}</span>;
}

/** Ligne d'un subordonné : nom, niveau, état, dossier (carte, ordre), avis. */
function ItemRow({
  item,
  running,
  current = false,
  nested,
  onOpen,
  onRead,
}: {
  item: DeclinationItem;
  running: boolean;
  current?: boolean;
  nested?: boolean;
  onOpen: (id: string, target: OpenTarget) => void;
  onRead?: () => void;
}) {
  const id = item.dossier?.id ?? null;
  const notices = visibleNotices(item.notices)
    .map((n) => ({ tone: noticeTone(n.code), text: noticeText(n, item.level) }))
    .filter((n) => n.text);
  return (
    <li className={clsx("py-2.5", nested ? "pr-4 pl-9" : "px-4")}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="min-w-[160px] flex-1">
          <p className="flex items-center gap-2 text-ui font-semibold text-ink">
            <span className="truncate">{unitDesignation(item.label)}</span>
            <Badge size="sm" tone="accent">
              {subordinateBadge(item.level, item.unit_label) ?? levelLabel(item.level)}
            </Badge>
          </p>
          <p className="truncate text-meta text-ink-muted">
            {item.dossier?.hidden ? t("Dossier créé, non partagé avec vous") : item.dossier?.name ?? t("Dossier à créer")}
          </p>
        </div>
        <ItemStatus item={item} running={running} current={current} />
        {id && (
          <div className="flex max-w-full flex-wrap items-center gap-1">
            {onRead && (
              <Button size="sm" variant="ghost" onClick={onRead}>
                <FileText size={14} /> {t("Lire son ordre")}
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => onOpen(id, "map")}>
              <MapIcon size={14} /> {t("Ouvrir la carte")}
            </Button>
          </div>
        )}
      </div>
      {(item.error || notices.length > 0) && (
        <ul className="mt-1.5 space-y-1">
          {item.error && (
            <li className="flex items-start gap-1.5 text-meta leading-snug text-danger">
              <CircleAlert size={13} className="mt-px shrink-0" aria-hidden /> {tServer(item.error)}
            </li>
          )}
          {notices.map((n, i) => (
            <li key={i} className={clsx("flex items-start gap-1.5 text-meta leading-snug", n.tone === "warn" ? "text-ink" : "text-ink-soft")}>
              <CircleAlert size={13} className={clsx("mt-px shrink-0", n.tone === "warn" ? "text-warn" : "text-accent")} aria-hidden /> {n.text}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Liste des subordonnés, rangés par brigade dans une division (par division dans un corps d'armée). */
function ItemList({
  items,
  running,
  onOpen,
  onRead,
}: {
  items: DeclinationItem[];
  running: boolean;
  onOpen: (id: string, target: OpenTarget) => void;
  onRead?: (exerciseId: string) => void;
}) {
  const read = (item: DeclinationItem) => (onRead && item.dossier?.id && item.status === "done" ? () => onRead(item.dossier!.id!) : undefined);
  // Subordonné en cours de rédaction : le premier qui attend (le serveur les rédige dans l'ordre).
  const current = running ? items.find((i) => i.status === "pending")?.key : undefined;
  return (
    <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
      {itemTree(items).map(({ item, children }) => (
        <li key={item.key}>
          <ul className="divide-y divide-line">
            <ItemRow item={item} running={running} current={item.key === current} onOpen={onOpen} onRead={read(item)} />
            {children.map((child) => (
              <ItemRow key={child.key} item={child} running={running} current={child.key === current} nested onOpen={onOpen}
                onRead={read(child)} />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Avancement d'une exécution : barre, subordonnés rédigés un à un (chacun ouvrable dès qu'il est prêt). */
function Progress({
  state,
  resumeFailed,
  primary,
  onOpen,
}: {
  state: DeclinationState;
  resumeFailed: boolean;
  primary: ReactNode;
  onOpen: (id: string, target: OpenTarget) => void;
}) {
  const run = state.declination!;
  const { done, total } = run.progress;
  const interrupted = run.status === "interrupted";
  return (
    <div className="mx-auto w-full max-w-[720px] space-y-4 px-4 py-6 sm:px-6">
      <div className="rounded-xl border border-line bg-surface px-4 py-4 shadow-card">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-title font-semibold text-ink">
            {interrupted ? t("Génération interrompue") : <Waiting>{t("Rédaction des ordres subordonnés…")}</Waiting>}
          </h2>
          <span className="shrink-0 text-ui text-ink-soft tabular-nums">{t("{done} sur {total}", { done, total })}</span>
        </div>
        <ProgressBar
          className="mt-3"
          value={total ? done / total : 0}
          tone={interrupted ? "warn" : "run"}
          label={t("Ordres rédigés : {done} sur {total}", { done, total })}
        />
        <p className="mt-3 text-meta leading-snug text-ink-muted">
          {interrupted
            ? resumeFailed
              ? t("Reprenez la génération : elle repart où elle s'est arrêtée, sans refaire ce qui est fait.")
              : <Waiting>{t("Reprise en cours, là où la génération s'est arrêtée…")}</Waiting>
            : t("Chaque subordonné reçoit son dossier puis son OPO. Vous pouvez quitter la vue : la génération continue sur le serveur.")}
        </p>
        <Presence show={interrupted && resumeFailed}>
          {interrupted && resumeFailed && (
            <Motion origin="top" className="mt-3 flex justify-end">
              {primary}
            </Motion>
          )}
        </Presence>
      </div>
      <ItemList items={run.items} running={!interrupted} onOpen={onOpen} />
    </div>
  );
}

/** Document regroupé : sommaire (vue d'ensemble, parties communes, une partie par subordonné), section choisie. */
function GroupedDocumentPane({
  exerciseId,
  state,
  onOpen,
  onUpdate,
}: {
  exerciseId: string;
  state: DeclinationState;
  onOpen: (id: string, target: OpenTarget) => void;
  onUpdate: () => void;
}) {
  const run = state.declination!;
  const wide = useMediaQuery("(min-width: 1024px)");
  const doc = useQuery({
    queryKey: ["grouped", exerciseId, run.finished_at],
    queryFn: () => api.groupedDocument(exerciseId),
    enabled: run.document,
    staleTime: 60_000,
  });
  const { index, sections } = useMemo(() => groupedSections(doc.data), [doc.data]);
  const [selected, setSelected] = useState<string>(OVERVIEW);
  const scroller = useRef<HTMLDivElement>(null);
  const section = sections.find((s) => s.id === selected) ?? null;
  const order = [OVERVIEW, ...sections.map((s) => s.id)];
  const position = order.indexOf(section ? section.id : OVERVIEW);
  const choose = (id: string) => {
    setSelected(id);
    scroller.current?.scrollTo({ top: 0 });
  };
  const readUnit = (id: string) => {
    const found = sections.find((s) => s.kind === "unit" && s.exerciseId === id);
    if (found) choose(found.id);
  };
  const otherLanguage = run.language !== lang();

  const callouts = (
    <div className="space-y-2">
      {run.status === "failed" && (
        <Callout tone="danger" title={t("Génération inachevée")}>
          {run.error ? tServer(run.error) : t("Relancez la génération : elle reprend les ordres non rédigés.")}
        </Callout>
      )}
      {run.outdated && (
        <Callout tone="warn" title={t("L'ordre de l'échelon supérieur a changé depuis")}>
          {run.outdated_items?.length
            ? t("Ordre modifié depuis : {names}. Chaque ordre subordonné le suivra à son ouverture ; vous pouvez aussi les mettre tous à jour maintenant. Ce que vous y avez modifié vous-même est gardé.", { names: run.outdated_items.join(", ") })
            : t("Chaque ordre subordonné suivra le nouvel ordre à son ouverture ; vous pouvez aussi les mettre tous à jour maintenant. Ce que vous y avez modifié vous-même est gardé.")}
        </Callout>
      )}
      {run.status === "done" && Boolean(run.new_items?.length) && (
        <Callout tone="warn" title={t("Nouveau subordonné sans ordre")}>
          {t("Sans ordre décliné : {names}. Mettez à jour les ordres subordonnés pour rédiger le sien ; les autres ordres gardent ce que vous y avez modifié vous-même.", { names: (run.new_items ?? []).join(", ") })}
        </Callout>
      )}
      {otherLanguage && (
        <Callout
          tone="info"
          action={
            <Button size="sm" variant="secondary" onClick={onUpdate}>
              {lang() === "en" ? t("Rédiger en anglais") : t("Rédiger en français")}
            </Button>
          }
        >
          {run.language === "en" ? t("Ordres rédigés en anglais.") : t("Ordres rédigés en français.")}
        </Callout>
      )}
    </div>
  );

  const indexList = (
    <nav aria-label={t("Sommaire du document regroupé")} className="space-y-0.5">
      <IndexButton active={!section} onClick={() => choose(OVERVIEW)} depth={0}>
        {t("Vue d'ensemble")}
      </IndexButton>
      {index.map((entry, i) =>
        entry.type === "label" ? (
          <SectionLabel key={`l${i}`} as="p" inset className={clsx("pt-3 pb-1", entry.depth ? "pl-4" : "pl-2")}>
            {entry.text}
          </SectionLabel>
        ) : (
          <IndexButton
            key={entry.section.id}
            active={section?.id === entry.section.id}
            onClick={() => choose(entry.section.id)}
            depth={entry.section.depth}
            review={entry.section.review.length}
          >
            {entry.section.title}
          </IndexButton>
        ),
      )}
    </nav>
  );

  return (
    <div className="flex h-full min-h-0">
      {wide && <aside className="w-[300px] shrink-0 overflow-y-auto border-r border-line bg-canvas px-3 py-3">{indexList}</aside>}
      <div className="flex min-w-0 flex-1 flex-col">
        {!wide && (
          <div className="flex shrink-0 items-center gap-1 border-b border-line bg-surface px-3 py-2">
            <IconButton size="sm" onClick={() => choose(order[position - 1]!)} disabled={position <= 0}
              title={t("Partie précédente")} aria-label={t("Partie précédente")}>
              <ChevronLeft size={16} />
            </IconButton>
            <div className="min-w-0 flex-1">
              <Select className={inputClass} value={section ? section.id : OVERVIEW} onChange={(e) => choose(e.target.value)}
                aria-label={t("Sommaire du document regroupé")}>
                <option value={OVERVIEW}>{t("Vue d'ensemble")}</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.review.length ? t("{title} (à revoir)", { title: s.title }) : s.title}
                  </option>
                ))}
              </Select>
            </div>
            <IconButton size="sm" onClick={() => choose(order[position + 1]!)} disabled={position < 0 || position >= order.length - 1}
              title={t("Partie suivante")} aria-label={t("Partie suivante")}>
              <ChevronRight size={16} />
            </IconButton>
          </div>
        )}
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[820px] space-y-4 px-4 py-5 sm:px-6">
            {callouts}
            {!section ? (
              <Motion key={OVERVIEW} origin="fade" className="space-y-4">
                <div>
                  <h2 className="text-heading leading-snug font-semibold text-ink">{doc.data?.title ?? t("Document regroupé")}</h2>
                  {doc.data?.subtitles.map((s) => (
                    <p key={s} className="text-ui text-ink-soft">
                      {s}
                    </p>
                  ))}
                </div>
                {run.notices.map((n, i) =>
                  noticeText(n) ? (
                    <Callout key={i} tone={noticeTone(n.code)}>
                      {noticeText(n)}
                    </Callout>
                  ) : null,
                )}
                <ItemList items={run.items} running={false} onOpen={onOpen} onRead={readUnit} />
                {doc.isError && <Callout tone="danger">{tServer(doc.error.message)}</Callout>}
              </Motion>
            ) : (
              <Motion key={section.id} origin="fade">
                <SectionBody
                  section={section}
                  items={run.items}
                  onOpen={onOpen}
                  sketch={(childId) => api.groupedSketchUrl(exerciseId, childId, run.finished_at)}
                />
              </Motion>
            )}
            {doc.isLoading && run.document && <EmptyState variant="loading" compact title={t("Lecture du document regroupé…")} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function IndexButton({
  active,
  onClick,
  depth,
  review = 0,
  children,
}: {
  active: boolean;
  onClick: () => void;
  depth: number;
  review?: number;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active || undefined}
      className={clsx(
        "flex min-h-8 w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-ui leading-snug transition-colors",
        depth === 2 ? "pl-6" : depth === 1 ? "pl-4" : "pl-2",
        active ? "bg-selected font-medium text-ink" : "text-ink-soft hover:bg-hover hover:text-ink",
      )}
    >
      <span className="min-w-0 flex-1 break-words">{children}</span>
      {review > 0 && (
        <Badge size="sm" tone="warn" title={t("Paragraphes à revoir")}>
          {review}
        </Badge>
      )}
    </button>
  );
}

/** Croquis du subordonné, tel que le document le porte : il apparaît en fondu une fois chargé ; illisible, il
 *  s'efface et la légende reste, avec l'accès à la carte du dossier. */
function Sketch({ src, alt }: { src: string; alt: string }) {
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  if (state === "failed") return null;
  return (
    <div className="relative aspect-[10/7] w-full border-b border-line bg-canvas">
      {state === "loading" && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner decorative />
        </span>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setState("ready")}
        onError={() => setState("failed")}
        className={clsx("h-full w-full object-contain transition-opacity duration-(--gx-enter)", state === "ready" ? "opacity-100" : "opacity-0")}
      />
    </div>
  );
}

/** Une section du document : parties communes (dossier du chef) ou partie d'un subordonné (sa carte, son ordre). */
function SectionBody({
  section,
  items,
  onOpen,
  sketch,
}: {
  section: GroupedSection;
  items: DeclinationItem[];
  onOpen: (id: string, target: OpenTarget) => void;
  /** Adresse du croquis d'un subordonné. */
  sketch: (childId: string) => string;
}) {
  const item = items.find((i) => i.dossier?.id === section.exerciseId);
  const notices = visibleNotices(item?.notices ?? [])
    .map((n) => ({ code: n.code, tone: noticeTone(n.code), text: noticeText(n, section.level) }))
    .filter((n) => n.text);
  const unit = section.kind === "unit";
  return (
    <article className="space-y-3">
      <header className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-line pb-3">
        <div className="min-w-[200px] flex-1">
          <h2 className="text-heading leading-snug font-semibold text-ink">{section.title}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge tone="accent">{subordinateBadge(section.level, section.unitLabel) ?? levelLabel(section.level)}</Badge>
            {section.review.length > 0 && (
              <Badge dot tone="warn">
                {section.review.length <= 1
                  ? t("{n} paragraphe à revoir", { n: section.review.length })
                  : t("{n} paragraphes à revoir", { n: section.review.length })}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => onOpen(section.exerciseId, "order")}>
            <FileText size={14} /> {unit ? t("Ouvrir son ordre") : t("Ouvrir l'ordre du chef")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onOpen(section.exerciseId, "map")}>
            <MapIcon size={14} /> {unit ? t("Ouvrir sa carte") : t("Ouvrir la carte du chef")}
          </Button>
        </div>
      </header>
      {notices.map((n, i) => (
        <Callout
          key={i}
          tone={n.tone}
          action={
            n.code === "not_in_order" || n.code === "mission_fallback" ? (
              <Button size="sm" variant="secondary" onClick={() => onOpen(section.exerciseId, "mission")}>
                {t("Ouvrir ses Données initiales")}
              </Button>
            ) : undefined
          }
        >
          {n.text}
        </Callout>
      ))}
      {/* Ordre d'un subordonné : ses pages A4, mêmes paragraphes que l'OPO du chef (08/10/2026) ; à défaut
          d'ordre rédigé, les paragraphes du document regroupé. */}
      {unit ? (
        <SubordinateOrder exerciseId={section.exerciseId} onOpen={onOpen}>
          {section.nodes.map((node, i) => (
            <Node key={i} node={node} onOpen={onOpen} sketch={sketch} />
          ))}
        </SubordinateOrder>
      ) : (
        section.nodes.map((node, i) => <Node key={i} node={node} onOpen={onOpen} sketch={sketch} />)
      )}
    </article>
  );
}

/** OPO n° 1 du dossier d'un subordonné, en pages A4 identiques à son PDF, en lecture seule. */
function SubordinateOrder({
  exerciseId,
  onOpen,
  children,
}: {
  exerciseId: string;
  onOpen: (id: string, target: OpenTarget) => void;
  children: ReactNode;
}) {
  // Unités de l'ordre du subordonné : leur nom survolé montre la mini-carte, comme dans un OPO ; « Voir sur la
  // carte » ouvre le dossier du subordonné sur sa carte, centrée sur l'unité (10/10/2026).
  const queryClient = useQueryClient();
  const deployment = useQuery({
    queryKey: ["deployment-of", exerciseId],
    queryFn: () => api.exerciseDeployment(exerciseId, 1),
    retry: false,
    staleTime: 60_000,
  });
  const mentions = useMemo(() => unitMentions(deployment.data?.units, "T1"), [deployment.data]);
  const peek = useUnitPeek((m) => {
    onOpen(exerciseId, "map");
    const ui = useUi.getState();
    ui.select({ layer: "unit", lngLat: m.lngLat, code: m.code });
    ui.requestFlyTo(m.lngLat, 13.5);
  });
  const order = useQuery({
    queryKey: ["order", exerciseId, 1],
    queryFn: () => api.order(exerciseId, 1),
    retry: false,
    staleTime: 30_000,
  });
  const layout = useQuery({
    queryKey: ["order-layout", exerciseId, 1, order.data?.version, order.data?.has_signature],
    queryFn: () => api.orderLayout(exerciseId, 1),
    enabled: Boolean(order.data),
    staleTime: 30_000,
  });
  if (order.isError || layout.isError) return <>{children}</>;
  if (!layout.data) return <EmptyState variant="loading" compact title={t("Mise en page…")} />;
  const base = `/api/exercises/${exerciseId}/order`;
  // Mêmes adresses que la vue de l'ordre : logo, signature et cartes des annexes (A4, C7, corps B et D).
  const exerciseVersion =
    queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === exerciseId)?.version ?? 0;
  const imageUrl = orderImageUrl(base, 1, order.data?.version ?? "", exerciseVersion);
  return (
    <div className="flex flex-col gap-8 pt-2">
      {layout.data.pages.map((p) => (
        <OrderPage
          key={p.number}
          page={p}
          layout={layout.data}
          imageUrl={imageUrl}
          activeField={null}
          editable={false}
          onPick={() => undefined}
          units={mentions}
          onUnitHover={peek.hover}
        />
      ))}
      {peek.element}
    </div>
  );
}

function Node({
  node,
  onOpen,
  sketch,
}: {
  node: GroupedNode;
  onOpen: (id: string, target: OpenTarget) => void;
  sketch: (childId: string) => string;
}) {
  if (node.kind === "heading")
    return node.level === 3 ? (
      <h3 className="flex flex-wrap items-center gap-2 pt-2 text-title font-semibold text-ink">
        {node.text}
        {node.meta?.review === true && (
          <Badge dot tone="warn" title={t("Texte généré à vérifier : l'ordre de l'échelon supérieur ne le donne pas ou ne lui correspond pas")}>
            {t("À revoir")}
          </Badge>
        )}
      </h3>
    ) : (
      <h4 className="pt-1 text-ui font-semibold text-ink-soft">{node.text}</h4>
    );
  if (node.kind === "map")
    return (
      <figure className="overflow-hidden rounded-lg border border-line bg-surface">
        {node.meta?.exercise_id && <Sketch src={sketch(node.meta.exercise_id)} alt={node.text} />}
        <figcaption className="flex flex-wrap items-center gap-3 px-3 py-2.5">
          <MapIcon size={16} className="shrink-0 text-friend" aria-hidden />
          <span className="min-w-[180px] flex-1 text-meta leading-snug text-ink-soft">{node.text}</span>
          {node.meta?.exercise_id && (
            <Button size="sm" variant="secondary" onClick={() => onOpen(node.meta!.exercise_id!, "map")}>
              {t("Ouvrir sa carte")} <ArrowRight size={14} />
            </Button>
          )}
        </figcaption>
      </figure>
    );
  if (Array.isArray(node.meta?.review))
    return (
      <Callout tone="warn" title={t("À revoir")}>
        {node.text}
      </Callout>
    );
  return <p className="text-ui leading-relaxed whitespace-pre-line text-ink">{node.text}</p>;
}
